"use client"

import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Badge } from "../ui/badge"
import { Alert, AlertDescription } from "../ui/alert"
import {
  Zap,
  AlertTriangle,
  Timer,
  Phone,
  CheckCircle2,
  Truck,
  Package,
  MapPin,
  RotateCcw,
  Info,
  ChevronDown,
  ChevronUp,
} from "lucide-react"
import type { SuspensionLineDto, PendingToSendLineDto, SuspensionsViewMode } from "../../types/suspensions"
import { formatPhone } from "../../lib/format-utils"
import { lineHasImpedimento } from "../../constants/suspensionOperationalReasons"

interface UrgentActionsSectionProps {
  viewMode: SuspensionsViewMode
  suspensions: SuspensionLineDto[]
  linesToSend: PendingToSendLineDto[]
  onViewLine: (lineId: string) => void
  onViewContract: (contractId: string) => void
  onViewContractSummary?: (contractId: string) => void
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
}

export function UrgentActionsSection({
  viewMode,
  suspensions,
  linesToSend,
  onViewLine,
  onViewContract,
  onViewContractSummary,
  expanded,
  onExpandedChange,
}: UrgentActionsSectionProps) {
  // Get first suspensions (highest priority - never suspended)
  const firstSuspensions = suspensions.filter(
    (s) => !lineHasImpedimento(s) && s.dataSuspensao === null && s.eligibleForUrgencyMetrics,
  )

  // Get urgent suspensions (1-3 days)
  const urgentSuspensions = suspensions
    .filter(
      (s) =>
        !lineHasImpedimento(s) &&
        s.diasRestantes !== null &&
        s.diasRestantes <= 3 &&
        s.eligibleForUrgencyMetrics,
    )
    .sort((a, b) => (a.diasRestantes || 0) - (b.diasRestantes || 0))

  // Get lines to suspend again (already suspended before, not first suspension)
  // IMPORTANTE: Não filtrar por credenciais - todas as linhas que precisam renovar suspensão devem aparecer aqui,
  // independentemente de terem credenciais ou não. As credenciais são necessárias apenas para executar a suspensão.
  const toSuspendAgain = suspensions.filter(
    (s) => !lineHasImpedimento(s) && s.dataSuspensao !== null && s.eligibleForUrgencyMetrics,
  )
  const contractGroupsToSuspend = toSuspendAgain.reduce(
    (acc, line) => {
      if (!acc[line.contractId]) {
        acc[line.contractId] = []
      }
      acc[line.contractId].push(line)
      return acc
    },
    {} as Record<string, SuspensionLineDto[]>,
  )

  const contractsToSuspendAgain = Object.entries(contractGroupsToSuspend)
    .map(([contractId, lines]) => {
      const minDaysRemaining = Math.min(...lines.map(l => l.diasRestantes ?? Infinity))
      return {
        contractId,
        contractNumber: lines[0].contractNumber,
        clientName: lines[0].clientName,
        lineCount: lines.length,
        daysRemaining: minDaysRemaining === Infinity ? null : minDaysRemaining,
        mostUrgentLine: lines.find(l => l.diasRestantes === minDaysRemaining) || lines[0]
      }
    })
    .sort((a, b) => {
      if (a.daysRemaining === null) return 1
      if (b.daysRemaining === null) return -1
      return a.daysRemaining - b.daysRemaining
    })

  // Get lines ready to ship (grouped by contract for SEDEX/PAC)
  const contractGroups = linesToSend.reduce(
    (acc, line) => {
      if (!acc[line.contractId]) {
        acc[line.contractId] = []
      }
      acc[line.contractId].push(line)
      return acc
    },
    {} as Record<string, PendingToSendLineDto[]>,
  )

  const readyToShip = Object.entries(contractGroups)
    .filter(([_, lines]) => lines.every((l) => l.chipCcid && l.deliveryAddress))
    .map(([contractId, lines]) => ({
      contractId,
      contractNumber: lines[0].contractNumber,
      clientName: lines[0].clientName,
      lineCount: lines.length,
      shippingType: lines.length >= 3 ? "SEDEX" : ("PAC" as const),
      address: lines[0].deliveryAddress!,
    }))

  const hasUrgentItems = firstSuspensions.length > 0 || urgentSuspensions.length > 0 || contractsToSuspendAgain.length > 0
  const isExpanded = expanded ?? hasUrgentItems
  const prioritySummary = [
    firstSuspensions.length > 0 ? `${firstSuspensions.length} primeira suspensão` : null,
    urgentSuspensions.length > 0 ? `${urgentSuspensions.length} urgente${urgentSuspensions.length === 1 ? "" : "s"}` : null,
    contractsToSuspendAgain.length > 0 ? `${contractsToSuspendAgain.length} renovação${contractsToSuspendAgain.length === 1 ? "" : "ões"}` : null,
    readyToShip.length > 0 ? `${readyToShip.length} envio${readyToShip.length === 1 ? "" : "s"} pronto${readyToShip.length === 1 ? "" : "s"}` : null,
  ].filter(Boolean).join(", ")

  // Tab "A Enviar" não usa esta seção (envio tem fluxo próprio); a página já oculta o componente,
  // mas o early return aqui mantém o componente seguro caso seja chamado por engano.
  if (viewMode === "tosend") return null

  // Para Por Entrega, mostramos um callout informativo mesmo sem itens urgentes — explicando
  // que o consultor é responsável pela suspensão (a equipe interna não vai abrir o Meu TIM).
  const showEntregaCallout = viewMode === "entrega"

  if (!hasUrgentItems && readyToShip.length === 0 && !showEntregaCallout) {
    return null
  }

  return (
    <div id="urgent-actions-wrapper" className="space-y-4 mb-6">
      {showEntregaCallout && (
        <Alert className="bg-orange-50 border-orange-200">
          <Info className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            Contratos <strong>Por Entrega</strong>: o consultor é responsável por realizar a suspensão depois que o
            cliente recebe o chip TIM. A equipe interna acompanha aqui apenas para cobrar quando passa do prazo.
          </AlertDescription>
        </Alert>
      )}

      {hasUrgentItems || readyToShip.length > 0 ? (
        <button
          type="button"
          className="w-full flex items-center justify-between gap-3 rounded-lg border bg-white px-4 py-3 text-left shadow-sm hover:bg-muted/40 transition-colors"
          onClick={() => onExpandedChange?.(!isExpanded)}
        >
          <span className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-fuchsia-500" />
            <span className="text-lg font-semibold text-foreground">Ações Prioritárias</span>
          </span>
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            {prioritySummary || "sem pendências críticas"}
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </span>
        </button>
      ) : null}

      {isExpanded && <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* First Suspensions - Highest Priority */}
        {firstSuspensions.length > 0 && (
          <Card
            id="first-suspension-section"
            className="bg-gradient-to-br from-fuchsia-50 to-pink-50 border-fuchsia-200 border-2 flex flex-col"
          >
            <CardHeader className="pb-2 flex-shrink-0">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-fuchsia-700">
                <Zap className="h-4 w-4" />
                Primeira Suspensão ({firstSuspensions.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 flex-1 overflow-y-auto max-h-[400px] pr-2">
              {firstSuspensions.map((line) => (
                <div
                  key={line.id}
                  className="p-2 bg-white/70 rounded-lg border border-fuchsia-200 hover:bg-white cursor-pointer transition-colors"
                  onClick={() => onViewContractSummary ? onViewContractSummary(line.contractId) : onViewLine(line.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Phone className="h-3.5 w-3.5 text-fuchsia-500" />
                      <span className="text-sm font-medium">
                        {line.numero ? formatPhone(line.ddd, line.numero) : "Sem número"}
                      </span>
                    </div>
                    <Badge className="bg-fuchsia-100 text-fuchsia-700 text-[10px]">NUNCA SUSPENSA</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    {line.clientName} - {line.contractNumber}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Urgent Suspensions */}
        {urgentSuspensions.length > 0 && (
          <Card id="urgent-section" className="bg-gradient-to-br from-red-50 to-orange-50 border-red-200 border-2 flex flex-col">
            <CardHeader className="pb-2 flex-shrink-0">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-4 w-4" />
                Vencem em até 3 dias ({urgentSuspensions.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 flex-1 overflow-y-auto max-h-[400px] pr-2">
              {urgentSuspensions.map((line) => (
                <div
                  key={line.id}
                  className="p-2 bg-white/70 rounded-lg border border-red-200 hover:bg-white cursor-pointer transition-colors"
                  onClick={() => onViewContractSummary ? onViewContractSummary(line.contractId) : onViewLine(line.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Timer className="h-3.5 w-3.5 text-red-500" />
                      <span className="text-sm font-medium">
                        {line.numero ? formatPhone(line.ddd, line.numero) : "Sem número"}
                      </span>
                    </div>
                    <Badge className="bg-red-100 text-red-700 text-[10px]">
                      {line.diasRestantes} {line.diasRestantes === 1 ? "DIA" : "DIAS"}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 truncate">{line.clientName}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Renovar Suspensão */}
        {contractsToSuspendAgain.length > 0 && (
          <Card
            id="renew-suspension-section"
            className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200 border-2 flex flex-col"
          >
            <CardHeader className="pb-2 flex-shrink-0">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-amber-700">
                <RotateCcw className="h-4 w-4" />
                Renovar Suspensão ({contractsToSuspendAgain.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 flex-1 overflow-y-auto max-h-[400px] pr-2">
              {contractsToSuspendAgain.map((contract) => (
                <div
                  key={contract.contractId}
                  className="p-2 bg-white/70 rounded-lg border border-amber-200 hover:bg-white cursor-pointer transition-colors"
                  onClick={() => onViewContractSummary ? onViewContractSummary(contract.contractId) : onViewContract(contract.contractId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Phone className="h-3.5 w-3.5 text-amber-500" />
                      <span className="text-sm font-medium">{contract.contractNumber}</span>
                    </div>
                    <Badge className={`text-[10px] ${
                      contract.daysRemaining !== null && contract.daysRemaining <= 3
                        ? "bg-red-100 text-red-700"
                        : contract.daysRemaining !== null && contract.daysRemaining <= 7
                        ? "bg-orange-100 text-orange-700"
                        : "bg-amber-100 text-amber-700"
                    }`}>
                      {contract.daysRemaining !== null 
                        ? `${contract.daysRemaining} ${contract.daysRemaining === 1 ? "DIA" : "DIAS"}`
                        : "SEM PRAZO"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-muted-foreground truncate">{contract.clientName}</p>
                    <span className="text-xs text-amber-600 font-medium">
                      {contract.lineCount} {contract.lineCount === 1 ? "linha" : "linhas"}
                    </span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Ready to Ship */}
        {readyToShip.length > 0 && (
          <Card
            id="ready-to-ship-section"
            className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200 border-2 flex flex-col"
          >
            <CardHeader className="pb-2 flex-shrink-0">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="h-4 w-4" />
                Prontos para Enviar ({readyToShip.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 flex-1 overflow-y-auto max-h-[400px] pr-2">
              {readyToShip.map((contract) => (
                <div
                  key={contract.contractId}
                  className="p-2 bg-white/70 rounded-lg border border-emerald-200 hover:bg-white cursor-pointer transition-colors"
                  onClick={() => onViewContractSummary ? onViewContractSummary(contract.contractId) : onViewContract(contract.contractId)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{contract.contractNumber}</span>
                    <Badge
                      className={`text-[10px] ${
                        contract.shippingType === "SEDEX" ? "bg-red-100 text-red-700" : "bg-sky-100 text-sky-700"
                      }`}
                    >
                      {contract.shippingType === "SEDEX" ? (
                        <>
                          <Truck className="h-3 w-3 mr-1" />
                          {contract.shippingType}
                        </>
                      ) : (
                        <>
                          <Package className="h-3 w-3 mr-1" />
                          {contract.shippingType}
                        </>
                      )}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-muted-foreground truncate">{contract.clientName}</p>
                    <span className="text-xs text-emerald-600 font-medium">
                      {contract.lineCount} {contract.lineCount === 1 ? "linha" : "linhas"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <MapPin className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground truncate">
                      {contract.address.cidade}/{contract.address.estado}
                    </span>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>}
    </div>
  )
}

